export interface ApiErrorEnvelope {
    error: {
        code: string;
        message: string;
        details?: Record<string, unknown>;
    };
}

export class ApiClientError extends Error {

    public readonly status: number;
    public readonly code: string;
    public readonly details: Record<string, unknown> | undefined;

    constructor(status: number, code: string, message: string, details?: Record<string, unknown>) {

        super(message);
        this.name = "ApiClientError";
        this.status = status;
        this.code = code;
        this.details = details;
    }
}

type UnauthorizedHandler = () => void;

let unauthorizedHandler: UnauthorizedHandler | null = null;

// app shell registers a global 401 handler once. it clears the auth/me
// cache and navigates to /login. unset by tests via setUnauthorizedHandler(null).
export function setUnauthorizedHandler(handler: UnauthorizedHandler | null): void {

    unauthorizedHandler = handler;
}

export interface ApiFetchOptions extends Omit<RequestInit, "body"> {
    json?: unknown;
    /** A non-JSON request body (e.g. `FormData` for a multipart upload). When set,
     *  the `content-type` header is left unset so the browser supplies the correct
     *  value (including the multipart boundary). Ignored when `json` is provided. */
    body?: BodyInit;
    /** Skip the global unauthorized handler (the redirect-to-/login side effect)
     *  on a 401. Used by flows that render their own inline auth error — e.g. the
     *  magic-link verify page, where an expired token should show a message in
     *  place rather than bounce the user away. */
    skipAuthRedirect?: boolean;
}

export async function apiFetch<T>(path: string, options: ApiFetchOptions = {}): Promise<T> {

    const { json, body: rawBody, headers, skipAuthRedirect, ...rest } = options;

    let response: Response;
    try {

        response = await fetch(path, {
            ...rest,
            credentials: "same-origin",
            headers: {
                ...(json !== undefined ? { "content-type": "application/json" } : {}),
                ...headers,
            },
            body: json !== undefined ? JSON.stringify(json) : rawBody,
        });
    } catch (cause) {

        // fetch rejects only on network-level failures — the server is unreachable
        // (API down, connection refused, DNS failure), the request was aborted, or
        // CORS blocked it. Surface a typed, actionable error here so callers don't
        // flatten a raw TypeError into a generic "something went wrong", and so a
        // transient blip is never mistaken for a 401 (no unauthorizedHandler call).
        throw new ApiClientError(
            0,
            "NETWORK_ERROR",
            "Couldn't reach the server. Check your connection and make sure the API is running, then try again.",
            { cause: cause instanceof Error ? cause.message : String(cause) },
        );
    }

    if (response.status === 204)
        return undefined as T;

    let body: unknown;
    try {

        body = await response.json();
    } catch {

        body = null;
    }

    if (response.status === 401) {

        // unless the caller opts out, run the global handler (clears the session
        // cache and redirects to /login) for an expired/absent session.
        if (!skipAuthRedirect)
            unauthorizedHandler?.();

        // surface the server's specific message ("Invalid or expired token",
        // "Invalid session", …) so callers that render err.message show something
        // actionable; fall back to a generic string when there's no envelope.
        const envelope = body as ApiErrorEnvelope | null;
        throw new ApiClientError(
            401,
            envelope?.error?.code ?? "UNAUTHORIZED",
            envelope?.error?.message ?? "Authentication required",
            envelope?.error?.details,
        );
    }

    if (!response.ok) {

        const envelope = body as ApiErrorEnvelope | null;
        const code = envelope?.error?.code ?? "UNKNOWN";
        const message = envelope?.error?.message ?? `HTTP ${response.status}`;
        const details = envelope?.error?.details;
        throw new ApiClientError(response.status, code, message, details);
    }

    return body as T;
}
